import React, { useState } from 'react'
import { Card, Steps, Button, message } from 'antd'
import GeneralForm from '../../Common/WizardSteps/GeneralForm'
import VisitTrackers from '../../Common/WizardSteps/VisitTrackers'
import Summary from '../../Common/WizardSteps/Summary'
import ExercisesManagement from './ExercisesManagement'
import PainMedicationManagement from './PainMedicationManagement'
import FunctionalAssessments from './FunctionalAssessments'
import PsychologicalSupport from './PsychologicalSupport'
import PatientEducation from './PatientEducation'
import { CarePlanTemplatesNewRecordTemplate } from '../../Chronic Care Plans/utils/templates'

const { Step } = Steps

const PlanTemplateForm = ({
  isNew = true,
  recordData = null,
  refreshView,
  planType = 'rehabilitation',
  planLabel = 'Rehabilitation Plan',
  isMonitoring = false
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState(() => {
    if (!recordData) {
      return {
        generalInfo: {},
        visitTrackers: [],
        exercises: [],
        painMedications: [],
        functionalAssessments: [],
        psychologicalSupport: [],
        patientEducation: []
      }
    }
    return recordData
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const steps = [
    {
      title: 'General Information',
      content: (
        <GeneralForm
          data={formData.generalInfo || {}}
          setData={(values) => handleStepDataChange('generalInfo', values)}
          planType='rehabilitation'
          planLabel={planLabel}
          isMonitoring={isMonitoring}
        />
      ),
      key: 'generalInfo'
    },
    {
      title: 'Therapy & Specialist Visits',
      content: (
        <VisitTrackers
          visits={formData.visitTrackers || []}
          handleAddTracking={(key, selection) => {
            try {
              // Parse the stringified JSON selection
              const parsedSelection = JSON.parse(selection)

              // Use the CarePlanTemplatesNewRecordTemplate to create a tracker with the correct format
              const newTracker = CarePlanTemplatesNewRecordTemplate(
                key, // 'visits'
                parsedSelection.key, // camelCase key for the specialist type
                parsedSelection.label // display label for the specialist type
              )

              // Add the new tracker to the existing trackers
              const updatedTrackers = [...(formData.visitTrackers || []), newTracker]
              handleStepDataChange('visitTrackers', updatedTrackers)
            } catch (error) {
              console.error('Error adding visit tracker:', error)
              message.error('Failed to add visit tracker')
            }
          }}
          handleUpdateTracker={(value, index, property, key) => {
            const updatedTrackers = [...(formData.visitTrackers || [])]
            if (updatedTrackers[index] && property) {
              updatedTrackers[index][property] = value
              handleStepDataChange('visitTrackers', updatedTrackers)
            }
          }}
          handleRemoveTracker={(index, key) => {
            const updatedTrackers = [...(formData.visitTrackers || [])]
            updatedTrackers.splice(index, 1)
            handleStepDataChange('visitTrackers', updatedTrackers)
          }}
          planType='rehabilitation'
          planLabel={planLabel}
          specialistTypes={[
            'Physical Therapist',
            'Occupational Therapist',
            'Speech Therapist',
            'Rehabilitation Specialist',
            'Orthopedic Surgeon',
            'Neurologist',
            'Pain Management Specialist',
            'Sports Medicine Physician',
            'Physiatrist'
          ]}
        />
      ),
      key: 'visitTrackers'
    },
    {
      title: 'Home Exercises & Activities',
      content: (
        <ExercisesManagement
          value={formData.exercises || []}
          onChange={(values) => handleStepDataChange('exercises', values)}
        />
      ),
      key: 'exercises'
    },
    {
      title: 'Medication & Pain Management',
      content: (
        <PainMedicationManagement
          value={formData.painMedication || []}
          onChange={(values) => handleStepDataChange('painMedication', values)}
        />
      ),
      key: 'painMedication'
    },
    {
      title: 'Functional Assessments',
      content: (
        <FunctionalAssessments
          value={formData.assessments || []}
          onChange={(values) => handleStepDataChange('assessments', values)}
        />
      ),
      key: 'assessments'
    },
    {
      title: 'Psychological & Emotional Support',
      content: (
        <PsychologicalSupport
          value={formData.psychologicalSupport || []}
          onChange={(values) => handleStepDataChange('psychologicalSupport', values)}
        />
      ),
      key: 'psychologicalSupport'
    },
    {
      title: 'Patient Instructions & Education',
      content: (
        <PatientEducation
          value={formData.patientEducation || []}
          onChange={(values) => handleStepDataChange('patientEducation', values)}
        />
      ),
      key: 'patientEducation'
    },
    {
      title: 'Summary & Confirmation',
      content: (
        <Summary
          generalData={formData.generalInfo || {}}
          trackersData={formData.exercises || []}
          visitsData={formData.visitTrackers || []}
          medicationsData={formData.painMedication || []}
          diagnosticsData={formData.assessments || []}
          planType={planType}
          planLabel={planLabel}
          isMonitoring={isMonitoring}
          setCurrentStep={setCurrentStep}
        />
      ),
      key: 'summary'
    }
  ]

  const handleStepDataChange = (key, values) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: values
    }))
  }

  const validateStep = (step) => {
    const currentStepData = formData[steps[step].key]
    let isValid = true
    let errorMessage = ''

    switch (steps[step].key) {
      case 'generalInfo':
        if (!currentStepData || !currentStepData.name || !currentStepData.description) {
          isValid = false
          errorMessage = 'Please complete all required fields in General Information.'
        }
        break
      case 'visitTrackers':
        // This is optional, so we don't need to validate
        break
      case 'exercises':
        if (!currentStepData || currentStepData.length === 0) {
          isValid = false
          errorMessage = 'Please add at least one exercise to the rehabilitation plan.'
        }
        break
      case 'summary':
        // Summary step is a review, no validation needed
        break
      default:
        // Other steps are optional
        break
    }

    if (!isValid) {
      message.error(errorMessage)
    }

    return isValid
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrev = () => {
    setCurrentStep(currentStep - 1)
  }

  const handleFinish = async () => {
    setIsSubmitting(true)
    try {
      // For now, we'll just log the formData to console

      // In a real scenario, we would call an API to save the plan
      // await saveRehabilitationPlan(formData);

      // For demo purposes, simulate a delay
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Call refreshView to update the list of plans
      if (refreshView) {
        refreshView()
      }

      message.success('Rehabilitation plan created successfully!')
    } catch (error) {
      message.error('Failed to create rehabilitation plan. Please try again.')
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card title={isNew ? 'Create New Rehabilitation Plan' : 'Edit Rehabilitation Plan'} bordered={false}>
      <Steps current={currentStep} size='small'>
        {steps.map((step) => (
          <Step key={step.key} title={step.title} />
        ))}
      </Steps>

      <div style={{ marginTop: 24, marginBottom: 24 }}>{steps[currentStep].content}</div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {currentStep > 0 && <Button onClick={handlePrev}>Previous</Button>}

        {currentStep < steps.length - 1 && (
          <Button type='primary' onClick={handleNext} style={{ marginLeft: 'auto' }}>
            Next
          </Button>
        )}

        {currentStep === steps.length - 1 && (
          <Button type='primary' onClick={handleFinish} loading={isSubmitting} style={{ marginLeft: 'auto' }}>
            {isNew ? 'Create Plan' : 'Update Plan'}
          </Button>
        )}
      </div>
    </Card>
  )
}

export default PlanTemplateForm
